import { ILandingStore, useLandingStore } from "src/modules/pe-landing-module/data/store"
import { IPrimitiveProps } from "../../../section/Primitive"
import { Button, ButtonVariant, Collapse, Icon, Popover, PopoverInteractionKind, Position } from "@blueprintjs/core"
import { HasId, IImageData } from "src/libs/interfaces/layouts"
import { MediaChooser } from "src/libs/useful"
import { HISTORY_TYPE, IHistoryStep, ILandingContent, ILandingSection, ImageContent, LANDING_TYPE_CONTENT } from "src/modules/pe-landing-module/data/interfaces"
import { MouseEvent, useEffect, useRef, useState } from "react"
import FieldInput, { SCALAR_TYPES } from "src/libs/scalars"
import { __ } from "src/libs/utilities"  
import __description from "../../../../../assets/img/description-1.svg"
import __link from "../../../../../assets/img/link.svg"
import __gradient from "../../../../../assets/img/gradient.svg" 
import __sizes from "../../../../../assets/img/sizes.svg"
import __hover from "../../../../../assets/img/hover.svg" 
import updateHistory from "../../utils/updateHistory"

/* Protopia Ecosystem component */
export interface IImageEditorProps extends IPrimitiveProps {
   children: JSX.Element
   className: string
   sizes: IImageData 
}
const ImageEditor = (props: IImageEditorProps): JSX.Element => {
    const ref = useRef<HTMLDivElement>()
    const currentLanding = useLandingStore((state: ILandingStore) => state.currentLanding )
    const isEdit: boolean = useLandingStore((state: ILandingStore) => state.isEdit )
    const primitive: ImageContent = props.primitive as ImageContent
    const [ data, setData]              = useState<any>(primitive.data || {})
    const [isOpenSrc, setIsOpenSrc]     = useState<boolean>(false)
    const [isOpenSizes, setIsOpenSizes] = useState<boolean>(false)
    const [isOpenTitle, setIsOpenTitle] = useState<boolean>(false)
    const [isOpenLink, setIsOpenLink] = useState<boolean>(false)
    const [isOpenHoverSize, setIsOpenHoverSize] = useState<boolean>(false)

    useEffect(() => {
        setData( props.primitive?.data || {} )
    }, [ props.primitive ])

    const onSrc = ( value: string ) => {
        onChange( value, "src", "" )
    }
    const onChange = ( value: any, field: string, dop: any ) => {
        const newVal = {
            ...data,
            [field]: value
        }
        setData( newVal ) 
        const sectionElem: HTMLDivElement = (ref.current! as any).closest(`.landing-section-container.landing-gap`) 
        const dataElem: HTMLDivElement = (ref.current! as any).closest(`[data-type][data-content-id]`) 
        const dataType = dataElem.getAttribute("data-type")
        const dataId = dataElem.getAttribute("data-content-id") 
        const sectionId = sectionElem.getAttribute("id") 
        const step: IHistoryStep = { 
            data: null,
            prevData: null,
            type: HISTORY_TYPE.SECTION,
            title : "Edit image"
        }
        let newSection: ILandingSection = currentLanding.sections.find((sec: ILandingSection) => sec.id === sectionId) 
        step.prevData = JSON.parse(JSON.stringify(newSection))
        step.data = newSection = {
            ...newSection,
            data: newSection.data.map((dt: ILandingContent) => {  
                if(dataId === dt.id) {
                    switch(dataType) {
                        case LANDING_TYPE_CONTENT.FEATURES_CARDS:
                        case LANDING_TYPE_CONTENT.CARDS:
                            if( props.field === "thumbnail") { 
                                const newF = {
                                    ...dt,
                                    thumbnail: {
                                        ...dt.thumbnail,
                                        ...newVal,
                                        data: {
                                            ...dt.thumbnail.data,
                                            ...newVal
                                        }
                                    }
                                }
                                return newF
                            }
                            else {
                                alert(`Unknown data type: ${ dt.type } and field ${ props.field }`)
                            }
                            return dt
                        case LANDING_TYPE_CONTENT.IMAGE:
                            return newVal
                        case LANDING_TYPE_CONTENT.PASSAGE:  
                            return {
                                ...dt,
                                items: dt.items.map((item: HasId) => {
                                    if(item.id === data.id) {
                                        return newVal
                                    }
                                    return item
                                })
                            }
                        default:
                            alert(`Unknown data type: ${ dt.type }`)
                            return dt
                    }
                }
                return dt
            })
        }
        const sections: ILandingSection[] = currentLanding.sections.map((section: ILandingSection) => {
            if( section.id === newSection.id ) {
                return newSection
            }
            return section
        })
        updateHistory( step, { ...currentLanding, sections: sections } ) 
    }
    const closeAllCollapses = () => {
        setIsOpenSrc(false) 
        setIsOpenTitle(false) 
        setIsOpenSizes(false) 
        setIsOpenLink(false) 
        setIsOpenHoverSize(false) 
    } 
    const onClick = (evt: MouseEvent) => {
        closeAllCollapses()
        if(!!evt) {
            evt.preventDefault()
            // evt.stopPropagation()
        }
	    return false
    }

    const HEIGHT    = 100
    const WIDTH     = 170
    const ICON_SIZE = 14
    return isEdit
        ?
        <Popover
            position={Position.TOP_LEFT}
            hasBackdrop={false} 
            interactionKind={PopoverInteractionKind.CLICK}
            portalClassName="z-index-80" 
            className="w-100"
            modifiers={{ 
                arrow: { enabled: false },
                flip: { enabled: true },
                preventOverflow: { enabled: false },
                offset: { enabled: true, options: {
                    offset: [ 0, 4],
                } }, 
            }}
            autoFocus={false}
            content={
                <div className="pe-text-editor-tools-container">
                    <Collapse isOpen={isOpenSrc} className="w-100">
                        <div className="landing-primitive-editor image-chooser-container p-3">
                            <MediaChooser
                                padding={ 0 }
                                url={ data.src || "" }
                                id={`image-chooser-${primitive.id}`}
                                height={ HEIGHT }
                                width={ props.sizes.height ? props.sizes.width / props.sizes.height * HEIGHT : WIDTH }
                                onChange={ onSrc }
                            />
                        </div>
                    </Collapse>
                    <Collapse isOpen={isOpenSizes} className="w-100">
                        <div className="landing-primitive-editor image-chooser-container  p-3">
                            <FieldInput
                                type={ SCALAR_TYPES.CSS_MAGNITUDE }
                                title={ __("Height") }
                                editable
                                value={ data.height }
                                field={ "height" }
                                notClear
                                onChange={ onChange }
                            />
                            <FieldInput
                                type={ SCALAR_TYPES.CSS_MAGNITUDE }
                                title={ __("Width") }
                                editable
                                value={ data.width }
                                field={ "width" }
                                notClear
                                onChange={ onChange }
                            />
                        </div>
                    </Collapse>
                    <Collapse isOpen={isOpenHoverSize} className="w-100">
                        <div className="landing-primitive-editor image-chooser-container  p-3">
                            <div className="pl-3">
                                <FieldInput
                                    type={ SCALAR_TYPES.SLIDER }
                                    subtitle={ __( "Hover size" ) }
                                    editable
                                    value={ data.hover_size }
                                    min={ -5 }
                                    max={ 20 }
                                    stepSize={ 1 }
                                    labelStepSize={ 5 }
                                    disabledLabel
                                    values={[
                                        {
                                            _id: -3,
                                            value: -3,
                                            title: "Weak away"
                                        },
                                        {
                                            _id: 0,
                                            value: 0,
                                            title: "None"
                                        },
                                        {
                                            _id: 2,
                                            value: 2,
                                            title: "Weak"
                                        },
                                        {
                                            _id: 5,
                                            value: 5,
                                            title: "Noticeable"
                                        },
                                        {
                                            _id: 10,
                                            value: 10,
                                            title: "Significant"
                                        },
                                        {
                                            _id: 15,
                                            value: 15,
                                            title: "Significant"
                                        },
                                        {
                                            _id: 20,
                                            value: 20,
                                            title: "Giant"
                                        },
                                    ]}
                                    field={ "hover_size" }
                                    notClear
                                    onChange={ onChange }
                                />
                            </div>
                        </div>
                    </Collapse>
                    <Collapse isOpen={isOpenTitle} className="w-100 overflow-hidden">
                        <div className="landing-primitive-editor max-width-auto p-3 overflow-hidden">
                            <div className="pl-3">
                                <FieldInput
                                    type={ SCALAR_TYPES.HTML }
                                    title={ __("Title") }
                                    editable
                                    value={ data.description_title }
                                    field={ "description_title" }
                                    notClear
                                    onChange={ onChange }
                                />
                                <FieldInput
                                    type={ SCALAR_TYPES.HTML }
                                    subtitle={ __("Description") }
                                    editable
                                    value={ data.description }
                                    field={ "description" }
                                    rows={2}
                                    notClear
                                    noDefault
                                    onChange={ onChange }
                                />
                            </div>
                        </div>
                    </Collapse>
                    <Collapse isOpen={isOpenLink} className="w-100">
                        <div className="landing-primitive-editor image-chooser-container  p-3">
                            <div className="pl-3">
                                <FieldInput
                                    type={ SCALAR_TYPES.STRING }
                                    subtitle={ __("Link URL") }
                                    editable
                                    value={ data.link_url }
                                    field={ "link_url" }
                                    notClear
                                    onChange={ onChange }
                                />
                            </div>
                        </div>
                    </Collapse>
                    <div className="d-flex">
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Image source" ) }  className="btn-stretch p-0" onClick={() => { 
                            closeAllCollapses()
                            setIsOpenSrc( true ) 
                        }} >
                            {
                                data.src 
                                    ?
                                    <img src={data.src } alt="" className="" style={{ minWidth: 24,  width: 24, minHeight: 24, margin: 3 }} /> 
                                    :
                                    <Icon icon="media" color="#FFFFFF" />
                            }
                            
                        </Button>
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Size" ) }   onClick={() => { 
                            closeAllCollapses()
                            setIsOpenSizes( true ) 
                        }} >
                            <img src={ __sizes } alt="" className="invert" style={{ minWidth: ICON_SIZE, minHeight: ICON_SIZE }} /> 
                        </Button> 
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Hover size" ) }   onClick={() => { 
                            closeAllCollapses()
                            setIsOpenHoverSize( true ) 
                        }} >
                            <img src={ __hover } alt="" className="invert" style={{ minWidth: ICON_SIZE, minHeight: ICON_SIZE }} /> 
                        </Button> 
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Description" ) } onClick={() => { 
                            closeAllCollapses()
                            setIsOpenTitle( true )  
                        }} >
                            <img src={ __description } alt="" className="invert" style={{ minWidth: ICON_SIZE, minHeight: ICON_SIZE }} /> 
                        </Button>
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Set contrast muar on bottom" ) }  onClick={() => { 
                            closeAllCollapses()  
                            onChange( !data.is_contrast_muar, "is_contrast_muar", "" )
                        }} >                           
                            <img src={ __gradient } alt="" className="invert" style={{ minWidth: ICON_SIZE, minHeight: ICON_SIZE }} /> 
                        </Button>
                        <Button variant={ButtonVariant.MINIMAL} title={ __( "Link URL" ) }   onClick={() => { 
                            closeAllCollapses()
                            setIsOpenLink( true ) 
                        }} >
                            <img src={ __link } alt="" className="invert" style={{ minWidth: ICON_SIZE, minHeight: ICON_SIZE }} /> 
                        </Button>
                        <Button icon="more" variant={ButtonVariant.MINIMAL} title={__("More")}  onClick={() => { 
                            closeAllCollapses() 
                        }} /> 
                        <Button icon="cross" variant={ButtonVariant.MINIMAL} title={__("Reset style")}  onClick={() => { 
                            closeAllCollapses() 
                        }} />
                        {/*
                            <div  className="pl-3 landing-primitive-editor image-chooser-container">
                                <FieldInput
                                    type={SCALAR_TYPES.BOOLEAN}
                                    enabledLabel={ __( "Set contrast muar on bottom" ) }
                                    disabledLabel={ __( "Set contrast muar on bottom" ) }
                                    editable
                                    value={ data.is_contrast_muar}
                                    field={ "is_contrast_muar" } 
                                    notClear
                                    onChange={ onChange }
                                />
                            </div>
                        </div> */}
                    </div>
                </div>
            }
        >
            <div className={`${ props.className}`} ref={ref}>
                <div className={`pe-image-editor-container ${ props.className}`} onClick={onClick}>
                    { props.children }
                </div>
            </div>
        </Popover>
        :
        <>
            { props.children }
        </>
}
export default ImageEditor